import { createFileRoute, Link } from "@tanstack/react-router";

import { FoundingCoachCard } from "~/components/founding-coach-card";
import { Button } from "~/components/ui/button";
import { Heading } from "~/components/ui/heading";
import type { CoachOverview } from "~/lib/coach-grouping";
import { coachQueryOptions } from "~/lib/query-options";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

import { FoundingCoachBadge } from "../../../../components/icons/founding-coach-badge";

function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export const Route = createFileRoute("/_public/coaches/founding/")({
  loader: async ({ context }) => {
    const coaches = await context.queryClient.ensureQueryData(
      coachQueryOptions.founding(),
    );
    return { shuffledCoaches: shuffleArray(coaches) as CoachOverview[] };
  },
  head: () => {
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
      {
        name: "Founding Coaches",
        url: "https://dreamcoachmatch.com/coaches/founding",
      },
    ]);
    return {
      ...createBrandMeta({
        title: "Founding Coaches",
        description:
          "Meet the founding coaches who shaped the Personal Development Summit. Discover their proven expertise and transformational approaches.",
        canonical: "https://dreamcoachmatch.com/coaches/founding",
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: FoundingCoachesPage,
  staticData: { title: "Founding Coaches" },
});

function FoundingCoachesPage() {
  const { shuffledCoaches } = Route.useLoaderData();

  return (
    <div className="relative overflow-hidden">
      <div
        className="absolute inset-0 -z-10 bg-linear-to-br from-brand-50 via-white to-white"
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <FoundingCoachBadge
          className="mx-auto mb-6 rounded-full shadow-lg shadow-brand-900/20"
          size={120}
        />
        <Heading
          isH1
          preHeadline="Early supporters of Dream Coach Match and the Personal Development Summit"
          headline="Meet our founding coaches"
          subheadline="Founding Coaches represent the first wave of coaching professionals who believed in the mission of Dream Coach Match and the Summit to make coaching accessible worldwide"
        />
        <div className="mt-10 grid gap-10 md:grid-cols-2">
          {shuffledCoaches.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-brand-200 bg-white/70 p-12 text-center text-lg text-gray-600">
              Founding coach profiles are being curated. Check back soon to meet
              the leaders of our community.
            </div>
          ) : (
            shuffledCoaches.map((coach) => (
              <FoundingCoachCard key={coach.id} coach={coach} />
            ))
          )}
        </div>
        <div className="mt-16 flex justify-center">
          <Link to="/discovery">
            <Button size="lg" variant="primary">
              Find your coach
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
